import ControlsAlt from './ControlsAlt';
import ForwardBackward from './ForwardBackward';
import NetInfo from '@react-native-community/netinfo';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import SeekBar from './SeekBar';
import { DeviceEventEmitter, StyleSheet, View } from 'react-native';
import { ParagraphMRegular } from '../../config/designSystem/typography';
import { StyledControls } from './StyledControls';
import { captureException } from '../../utils/sentry/sentry';
import { setAudioModeAsync, useAudioPlayer, useAudioPlayerStatus } from 'expo-audio';

const SKIP_SECONDS = 30;
const FAST_RATE = 1.2;
const NORMAL_RATE = 1;

/**
 * Migrated from expo-av (removed in SDK 55) to expo-audio.
 *
 * expo-audio is hook-first - there is no imperative `Audio.Sound` with
 * `setOnPlaybackStatusUpdate` - so this had to become a function component.
 *
 * UNITS: expo-av spoke in milliseconds, expo-audio speaks in SECONDS.
 * SeekBar's props (trackLength, currentPosition, onSeek) are still milliseconds,
 * so conversion happens at that boundary only.
 */
const Player = (props: any) => {
  const selectedTrack = props.selectedTrack ? props.selectedTrack : 0;
  const audioUrl = props.tracks?.[selectedTrack]?.audioUrl;

  const player = useAudioPlayer(audioUrl ? { uri: audioUrl } : null);
  const status = useAudioPlayerStatus(player);

  const [fast, setFast] = useState(false);
  const [connected, setConnected] = useState(true);
  // set while the user drags the seek bar, so playback resumes after the drag
  const previouslyPlayRef = useRef(false);

  const totalLengthMs = status?.duration ? status.duration * 1000 : 1;
  const currentPositionMs = status?.currentTime ? status.currentTime * 1000 : 0;
  const isPlaying = !!status?.playing;

  useEffect(() => {
    setAudioModeAsync({
      allowsRecording: false,
      playsInSilentMode: true,
      interruptionMode: 'doNotMix',
      interruptionModeAndroid: 'doNotMix',
      shouldPlayInBackground: true,
      shouldRouteThroughEarpiece: false,
    }).catch(captureException);
  }, []);

  // report progress upwards, as the expo-av onPlaybackStatusUpdate callback did
  useEffect(() => {
    try {
      if (!status?.isLoaded) return;
      props.setPositionMillis?.(currentPositionMs);
      if (status.duration) {
        props.setPositionPercent?.(status.currentTime / status.duration);
      }
    } catch (error) {
      captureException(error);
    }
  }, [currentPositionMs, status?.duration, status?.isLoaded]);

  const unload = useCallback(() => {
    try {
      // useAudioPlayer releases the native player on unmount; pausing is enough
      // here, and keeps the external 'unload' event behaving as before.
      if (status?.isLoaded && player.playing) {
        player.pause();
      }
    } catch (error) {
      captureException(error);
    }
  }, [player, status?.isLoaded]);

  useEffect(() => {
    const eventUnloadListener = DeviceEventEmitter.addListener('unload', () => unload());
    const networkEvent = NetInfo.addEventListener(state => {
      // isInternetReachable is null while NetInfo is still probing (it emits
      // that on every connectivity change): only a definite `false` means the
      // stream is at risk, otherwise the warning shows on a working connection
      setConnected(state.isConnected !== false && state.isInternetReachable !== false);
    });
    return () => {
      eventUnloadListener.remove();
      try {
        networkEvent();
      } catch (error) {
        captureException(error);
      }
    };
  }, [unload]);

  const recover = useCallback(async () => {
    try {
      if (!audioUrl) return;
      const positionSeconds = status?.currentTime ?? 0;
      const wasPlaying = player.playing;
      player.replace({ uri: audioUrl });
      if (positionSeconds) {
        await player.seekTo(positionSeconds);
      }
      // replace() leaves the new source paused; the old expo-av recover reloaded
      // with the previous shouldPlay state, so resume if we were playing.
      if (wasPlaying) {
        player.play();
      }
    } catch (error) {
      captureException(error);
    }
  }, [player, audioUrl, status?.currentTime]);

  const handlePlayPause = useCallback(async () => {
    try {
      if (player.playing) {
        player.pause();
      } else {
        player.play();
      }
    } catch (error) {
      captureException(error);
      recover();
    }
  }, [player, recover]);

  const handlePreviousTrack = useCallback(async () => {
    try {
      const current = player.currentTime ?? 0;
      await player.seekTo(current < SKIP_SECONDS ? 0 : current - SKIP_SECONDS);
    } catch (error) {
      captureException(error);
    }
  }, [player]);

  const handleNextTrack = useCallback(async () => {
    try {
      await player.seekTo((player.currentTime ?? 0) + SKIP_SECONDS);
    } catch (error) {
      captureException(error);
    }
  }, [player]);

  const handleFastRate = useCallback(() => {
    try {
      const next = fast ? NORMAL_RATE : FAST_RATE;
      player.setPlaybackRate(next, 'high');
      setFast(!fast);
    } catch (error) {
      captureException(error);
    }
  }, [player, fast]);

  // SeekBar hands us milliseconds
  const seek = useCallback(async (valueMs: number) => {
    try {
      await player.seekTo(valueMs / 1000);
      if (previouslyPlayRef.current) {
        player.play();
        previouslyPlayRef.current = false;
      }
    } catch (error) {
      captureException(error);
    }
  }, [player]);

  const sliding = useCallback(() => {
    try {
      if (player.playing) {
        player.pause();
        previouslyPlayRef.current = true;
      }
    } catch (error) {
      captureException(error);
    }
  }, [player]);

  const renderNoInternet = () => (<ParagraphMRegular>{props.noInternet}</ParagraphMRegular>);

  const height = props.height ? props.height : 400;
  const width = props.width ? props.width : 300;
  const backwardForwardHeight = Math.max(20, height * 0.07);
  const seekBarHeight = Math.max(0.2 * height, 50);
  const titleHeight = Math.max(0.2 * height, 50);
  const marginHeight = 10;
  const StyledControlHeight = height - marginHeight - seekBarHeight - titleHeight;
  const color = props.color ? props.color : '#bac6f2';

  return (
    <View style={styles.container}>
      <View style={{ marginBottom: marginHeight, justifyContent: 'center', alignItems: 'center' }}>
        {props.smallPlayer ? (<View>
          {!connected && renderNoInternet()}
          <ControlsAlt paused={!isPlaying} onPressPlay={handlePlayPause}
            onPressPause={handlePlayPause} color={color} />
        </View>) : (
          <View style={{ alignItems: 'center', justifyContent: 'center' }}>
            {!connected && renderNoInternet()}
            <StyledControls
              colorsArray={[props.color + '10', props.color + '20', props.color + '30']}
              radiusArray={[1, 0.7, 0.4]}
              opacityArray={[1, 1, 1]}
              child={<ControlsAlt paused={!isPlaying} onPressPlay={handlePlayPause}
                onPressPause={handlePlayPause} color={color} />
              }
              height={StyledControlHeight}
            />
            <View style={{ position: 'absolute', bottom: 0 }}>
              <ForwardBackward
                hideBack={props.hideBack}
                hideForward={props.hideForward}
                onBack={handlePreviousTrack}
                onForward={handleNextTrack}
                height={backwardForwardHeight}
                color={color}
                colorIcon='white' />
            </View>
          </View>)}
      </View>
      <View style={{ height: seekBarHeight }}>
        <SeekBar
          onSeek={seek}
          trackLength={totalLengthMs}
          onSlidingStart={sliding}
          currentPosition={currentPositionMs}
          onFastRate={handleFastRate}
          fast={fast}
          hideFastRate={true}
          minimumTrackTintColor={props.color}
          maximumTrackTintColor={'black'}
          textColor={'black'}
          sliderColor={props.color}
          width={width}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, alignItems: 'center', backgroundColor: 'transparent' },
});

export default Player;
